import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';

// Status colors matching Essential Practice
const STATUS_COLORS = {
  'Overdue': '#e74c3c',      // Red
  'Due Soon': '#f39c12',     // Orange
  'On Time': '#27ae60'       // Green
};

/**
 * Parse a date string (YYYY-MM-DD) without timezone shift
 */
const parseLocalDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/**
 * Format date to YYYY-MM-DD
 */
const formatDateISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculate chart data from raw form data
 */
export const processChartData = (rawData) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Process and deduplicate by client ID (keep most recent review)
  const clientMap = {};

  rawData.forEach(row => {
    const clientId = row.clientId;
    const lastReview = parseLocalDate(row.dateOfLastReview);

    // If client already exists, keep the more recent review
    if (!clientMap[clientId] || lastReview > parseLocalDate(clientMap[clientId].dateOfLastReview)) {
      clientMap[clientId] = row;
    }
  });

  // Convert to array and calculate derived fields
  const processed = Object.values(clientMap).map(row => {
    const lastReview = parseLocalDate(row.dateOfLastReview);
    const interval = row.targetReviewInterval;
    const nextDue = new Date(lastReview);
    nextDue.setDate(nextDue.getDate() + interval);

    const diffTime = nextDue.getTime() - today.getTime();
    const daysUntilDue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let status;
    if (daysUntilDue < 0) {
      status = 'Overdue';
    } else if (daysUntilDue <= 14) {
      status = 'Due Soon';
    } else {
      status = 'On Time';
    }

    return {
      clientId: row.clientId,
      dateOfLastReview: row.dateOfLastReview,
      targetReviewInterval: interval,
      nextReviewDue: formatDateISO(nextDue),
      daysUntilDue,
      status,
      color: STATUS_COLORS[status]
    };
  });

  // Sort by days until due (most urgent first)
  return processed.sort((a, b) => a.daysUntilDue - b.daysUntilDue);
};

/**
 * Custom tooltip component
 */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  const formatDate = (dateStr) => {
    // Parse without timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-4">
      <p className="font-bold text-gray-900 mb-2">{data.clientId}</p>
      <div className="text-sm space-y-1">
        <p><span className="text-gray-600">Status:</span> <span style={{ color: data.color, fontWeight: 600 }}>{data.status}</span></p>
        <p><span className="text-gray-600">Days Until Due:</span> <span className="font-medium">{data.daysUntilDue}</span></p>
        <p><span className="text-gray-600">Last Review:</span> <span className="font-medium">{formatDate(data.dateOfLastReview)}</span></p>
        <p><span className="text-gray-600">Next Due:</span> <span className="font-medium">{formatDate(data.nextReviewDue)}</span></p>
        <p><span className="text-gray-600">Review Interval:</span> <span className="font-medium">{data.targetReviewInterval} days</span></p>
      </div>
    </div>
  );
};

/**
 * Custom legend component
 */
const CustomLegend = () => {
  return (
    <div className="flex justify-center gap-6 mt-4">
      {Object.entries(STATUS_COLORS).map(([status, color]) => (
        <div key={status} className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: color }}></div>
          <span className="text-sm font-medium text-gray-700">{status}</span>
        </div>
      ))}
    </div>
  );
};

const TreatmentPlanReviewChart = ({ data, showLegend = true, chartHeight = 400, pdfMode = false }) => {
  // Process chart data with memoization
  const chartData = useMemo(() => processChartData(data), [data]);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8 text-center">
        <p className="text-gray-500">No data available to display</p>
      </div>
    );
  }

  // Calculate Y-axis domain with padding
  const minDays = Math.min(...chartData.map(d => d.daysUntilDue));
  const maxDays = Math.max(...chartData.map(d => d.daysUntilDue));
  const yMin = Math.min(minDays - 5, -10);
  const yMax = Math.max(maxDays + 5, 20);

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Chart Title */}
      <h3 className="text-xl font-bold text-gray-900 mb-2" style={pdfMode ? { fontSize: '28px' } : {}}>Treatment Plan Review Status by Client</h3>
      <p className="text-gray-600 text-sm mb-6" style={pdfMode ? { fontSize: '18px' } : {}}>Bars below the line indicate overdue reviews</p>

      {/* Chart */}
      <div className="w-full" style={{ height: `${chartHeight}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="clientId"
              tick={{ fontSize: 12, fontWeight: 600 }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis
              domain={[yMin, yMax]}
              tick={{ fontSize: 12, fontWeight: 600 }}
              label={{
                value: 'Days Until Due (Negative = Overdue)',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: 12, fontWeight: 600 }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" strokeWidth={2} />
            <Bar dataKey="daysUntilDue" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      {showLegend && <CustomLegend />}
    </div>
  );
};

export default TreatmentPlanReviewChart;
